import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import type { Theme } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import eq from 'webapp/helpers/eq';
import BlockSettingsMetricCompareComponent from 'webapp/components/block/settings/metric/compare';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';

import type BlockSetup from 'webapp/models-custom/block-setup';
import type DataSelect from 'webapp/models-custom/data-select';
import DsCompare from 'webapp/models-custom/ds-compare';

interface Args {
	blockSetup: BlockSetup;
	dataSelect: DataSelect;
	hideLabel?: boolean;
	showFormatAndFunction?: boolean;
	isDisabled?: boolean;
	theme?: Theme | string;
}

export default class BlockSettingsMetricComparesComponent extends Component<Args> {
	@service('analytics') declare analyticsService: Service.Analytics;

	get blockSetup(): BlockSetup {
		return this.args.blockSetup;
	}

	get maxCompares(): number {
		return this.blockSetup.visualization.allowSingleCompareOnPrimaryDs ? 1 : 5;
	}

	get shownCompares(): DsCompare[] {
		return this.args.dataSelect.limitations.compareCMs;
	}

	set shownCompares(value: DsCompare[]) {
		this.args.dataSelect.limitations.compareCMs = value;
	}

	get canAddCompare(): boolean {
		return this.shownCompares.length < this.maxCompares;
	}

	@action
	removeCompare(compare: DsCompare): void {
		const noneCompare = DsCompare.createFromFormula(null);
		this.changeCompare(compare, noneCompare);
	}

	@action
	changeCompare(oldCompare: DsCompare, newCompare: DsCompare): void {
		const eventData = Object.assign(
			{
				type: newCompare.typeName,
				origin: this.blockSetup.block.trackingOrigin,
			},
			this.args.dataSelect.getMetricTrackingData(),
		);
		this.analyticsService.event(TrackingEvent.DESIGNER_COMPARE_CHOSEN, eventData);

		this.blockSetup.changeCompare(this.args.dataSelect, oldCompare, newCompare);
	}

	@action
	addNewCompare(): void {
		const ds = this.args.dataSelect,
			newCompare = DsCompare.createFromFormula(null, ds.limitations.dateRange!);
		this.shownCompares = [...this.shownCompares, newCompare];
	}
	<template>
		<div class="bs__metric__compares">

			{{#unless @hideLabel}}
				<label>Compare with</label>
			{{/unless}}

			{{#if this.canAddCompare}}
				<a {{on "click" this.addNewCompare}} class="action-add">
					<CommonUiIconComponent @name="Plus" @size="sm" />
				</a>
			{{/if}}

			{{#each this.shownCompares as |compare idx|}}
				<BlockSettingsMetricCompareComponent
					@model={{compare}}
					@dataSelect={{@dataSelect}}
					@theme={{@theme}}
					@remove={{this.removeCompare}}
					@onChange={{this.changeCompare}}
					@showFormatAndFunction={{@showFormatAndFunction}}
					@hideRemove={{eq idx 0}}
					@isDisabled={{@isDisabled}}
				/>
			{{/each}}

		</div>
	</template>
}
